import React, { useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CMultiSelect,
  CRow,
  CTooltip,
} from '@coreui/react-pro'
import Bootbox from 'bootbox-react'
import moment from 'moment'
import { cilArrowLeft, cilInfo, cilLink, cilList, cilReload, cilStar } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import ToastComponent from 'src/components/common/TaostComponent'
import ScorecardService from 'src/service/ScorecardService'
import TeamPlayerService from 'src/service/TeamPlayerService'
import CompleteMarkModal from './CompleteMarkModal'
import { Link, useParams, useNavigate } from 'react-router-dom'
import Loader from 'src/components/Loader'
import MarkAsCompleteModal from './MarkAsCompleteModal'
import { useFormik } from 'formik'
import ScorecardGrid from './ScorecardGrid'
import ScorecardImport from './ScorecardImport'
import { CollapsibleImportCard } from 'src/components/common/ImportWizard'
import { hasScores } from './scorecardConfig'
import './ScoreCard.css'

// The borderless-alert InfoBox the import wizards use, copied rather than shared -
// same house convention as MiniStepper/StatTile.
const InfoBox = ({ children, color = 'info' }) => (
  <CAlert color={color} className="d-flex align-items-start gap-2 border-0 mb-3">
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

// A 2-day fixture keeps a separate scorecard row per player per innings. Used both by the innings
// selector on the page and by the import, which reads both innings out of one file.
const INNINGS_OPTIONS = [
  { value: 1, label: '1st Innings' },
  { value: 2, label: '2nd Innings' },
]

// Matches the fixture squad screen: one page of the game's players is enough to match a file
// against, and no game carries more than this.
const PLAYER_FETCH_LIMIT = 500

const CommonScorecard = (props) => {
  const navigate = useNavigate()
  const [commonScorecardLoading, setCommonScorecardLoading] = useState(false)
  const [finalScorecardModalVisible, setFinalScorecardModalVisible] = useState(false)
  const [statusValue, setStatusValue] = useState(props?.statusValue ?? 0)
  // save-scorecard writes status onto the fixture, so every save must carry the one the fixture
  // really has. The parent loads it after mount, so follow the prop rather than copying it once,
  // and keep a ref so a save fired in the same tick as a status change sends the new value.
  const statusRef = React.useRef(statusValue)
  const updateStatus = (value) => {
    statusRef.current = value
    setStatusValue(value)
  }
  useEffect(() => {
    updateStatus(props.statusValue ?? 0)
  }, [props.statusValue])
  const [defaultInning, setDefaultInning] = useState(1)
  const [completMarkModalVisible, setCompletMarkModalVisible] = useState(false)
  const [visible, setVisible] = useState(false)
  const [detailsModalVisible, setDetailsModalVisible] = useState(false)
  const [bonusModalVisible, setBonusModalVisible] = useState(false)
  const formikRef = React.useRef()
  const formik = useFormik({
    initialValues: {
      data: props.scorecardDetails,
      scorcard_link_name: props.fixtureDetails?.scorcard_link_name,
      scorcard_link_url: props.fixtureDetails?.scorcard_link_url,
      fall_of_wickets: props.fixtureDetails?.fall_of_wickets,
      match_report: props.fixtureDetails?.match_report,
      potm_match_status: props.fixtureDetails?.potm_match_status,
      match_status: '',
    },
    enableReinitialize: true,
    onSubmit: (data, actions) => {
      setCommonScorecardLoading(true)
      data.fixtureId = props.fixtureId
      data.potm = props.selectedValue
      data.status = statusRef.current
      data.inning = defaultInning
      props.setLoader(true)
      ScorecardService.saveScorecard(data).then((res) => {
        if (res.status === 200) {
          setCommonScorecardLoading(false)
          props.fetchData(defaultInning)
          ToastComponent(res.message, 'success')
        }
      })
    },
  })
  useEffect(() => {
    formikRef.current = formik
  }, [formik])

  const handleResetBackConfirm = () => {
    navigate(-1)
    return setShowConfirmation(false)
  }
  const handleResetBackCancel = () => {
    formikRef.current?.handleSubmit() // This should call the onSubmit
    return setShowConfirmation(false)
  }

  const param = useParams()
  const [completeMarkModalData, setCompleteMarkModalData] = useState([])
  // Completed only holds once markAsComplete has queued the finalisation job, which leaves this
  // page. Until then keep the status it replaced, so closing the review popups puts it back rather
  // than leaving Completed in the dropdown for the next save to send.
  const statusBeforeCompleteRef = React.useRef(null)
  const restoreStatusBeforeComplete = () => {
    if (statusBeforeCompleteRef.current === null) return
    updateStatus(statusBeforeCompleteRef.current)
    statusBeforeCompleteRef.current = null
  }
  useEffect(() => {
    // Confirm to Complete swaps one popup for the other in a single render, so both are only
    // hidden together when the user backed out.
    if (!completMarkModalVisible && !finalScorecardModalVisible) restoreStatusBeforeComplete()
  }, [completMarkModalVisible, finalScorecardModalVisible])
  const handleMatchStatus = (e) => {
    const previousStatus = statusRef.current
    updateStatus(e.target.value)
    const data = {}
    data.fixtureId = param.fixtureId
    data.status = e.target.value
    data.inning = defaultInning
    if (e.target.value === '2') {
      statusBeforeCompleteRef.current = previousStatus
      ScorecardService.getSavedScorecardData(data)
        .then((res) => {
          if (res.status === 200) {
            setCompleteMarkModalData(res.data)
            setCompletMarkModalVisible(true)
          } else {
            restoreStatusBeforeComplete()
          }
        })
        .catch(() => restoreStatusBeforeComplete())
    } else {
      formikRef.current?.handleSubmit() // This should call the onSubmit
      ScorecardService.changFixtureStatus(param.fixtureId, e.target.value)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            navigate(`/scorecard/${param.fixtureId}/${param.teamId}`)
          } else {
            ToastComponent(res.message, 'error')
          }
        })
        .catch((e) => {
          console.log('e', e)
          ToastComponent('Something went wrong.', 'error')
        })
    }
  }
  const changeInning = (e) => {
    setDefaultInning(e.target.value)
    props.fetchData(e.target.value)
  }

  // Detect changes in form values
  const [isFormModified, setIsFormModified] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  useEffect(() => {
    const handleFormChange = () => {
      const isModified = Object.keys(formik.initialValues).some(
        (key) => formik.values[key] !== formik.initialValues[key],
      )
      setIsFormModified(isModified)
    }

    // Trigger form change detection whenever formik values change
    handleFormChange()
  }, [formik]) // This will run on every form value change

  // Prevent navigation if form is modified
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormModified) {
        const message = 'You have unsaved changes, are you sure you want to leave?'
        e.returnValue = message // Standard for most browsers
        return message // For some browsers
      }
    }

    // Listen to the beforeunload event
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Cleanup the event listener on unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isFormModified]) // Only run when form is modified

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (e) => {
      if (isFormModified) {
        // Show the confirmation dialog
        setShowConfirmation(true)

        // Prevent back navigation until the user confirms
        e.preventDefault()
      }
    }

    // Add event listener for popstate (for back/forward buttons)
    window.addEventListener('popstate', handlePopState)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [isFormModified]) // Only re-run when form is modified

  // ---- presentation helpers ------------------------------------------------

  const teamId = props.teamId ?? props.param?.teamId ?? param.teamId

  const enteredCount = (props.scorecardDetails || []).filter((item) =>
    hasScores(item, props.scorecardColoumns),
  ).length
  const playerCount = (props.scorecardDetails || []).length
  const enteredPercent = playerCount ? Math.round((enteredCount / playerCount) * 100) : 0

  // Both scorecard-detail fields autosave through updateCustomScorecardField on
  // change. Formik is kept in step so the Save scores payload carries what is on
  // screen - save-scorecard writes these two columns too, and would otherwise put
  // the pre-edit value back.
  const handleScorecardDetailChange = (e, type) => {
    formik.handleChange(e)
    props.handleInputChange(e, type)
  }

  // Marker on the header buttons, so a panel that already holds something reads as
  // filled without being opened.
  const scorecardDetailsSet = Boolean(
    formik.values.scorcard_link_name || formik.values.scorcard_link_url,
  )
  const bonusPointsSet =
    (props.selectedValue?.length ?? 0) > 0 ||
    ['1', '2'].includes(String(formik.values.potm_match_status ?? ''))

  // Scores can be typed in one at a time in the grid below, or brought in from a spreadsheet.
  // The panel is closed by default and does not mount - or read a file - until it is opened,
  // so the ordinary path to this page is unchanged.
  const [importOpen, setImportOpen] = useState(false)
  const [importPlayers, setImportPlayers] = useState([])
  const [importInnings, setImportInnings] = useState([])
  const [importLoadFailed, setImportLoadFailed] = useState(false)

  const isTwoDay = props.fixtureDetails?.match_type_name === '2-Day'

  // Names the innings on screen wherever a message has to say what it is about to change.
  const clearTargetLabel = isTwoDay
    ? INNINGS_OPTIONS.find((option) => option.value === Number(defaultInning))?.label ||
      'this innings'
    : 'this innings'

  // A 2-day fixture holds a separate scorecard row per player per innings, and a match export
  // carries both innings in one file - while this page only ever holds the innings on screen. So
  // every innings the fixture has is read when the panel is opened, and the file decides which
  // innings a row is saved to rather than the selector above.
  //
  // The game's players come with them: a file is matched against all of them rather than against
  // this fixture's squad, so a player who turns up in a match export without having been picked
  // for the squad can still be imported - the import adds them.
  const importInningsOptions = useMemo(
    () => (isTwoDay ? INNINGS_OPTIONS : [{ value: 1, label: 'this innings' }]),
    [isTwoDay],
  )

  useEffect(() => {
    if (!importOpen) return undefined
    let cancelled = false
    setImportLoadFailed(false)
    Promise.all([
      TeamPlayerService.getTeamPlayer(
        0,
        PLAYER_FETCH_LIMIT,
        1,
        new URLSearchParams(),
        props.fixtureId,
        teamId,
      ),
      ...importInningsOptions.map((option) =>
        ScorecardService.scorecardDetail(props.fixtureId, option.value, teamId),
      ),
    ])
      .then(([playerRes, ...inningsRes]) => {
        if (cancelled) return
        if (inningsRes.some((res) => res?.status !== 200)) {
          setImportLoadFailed(true)
          return
        }
        setImportPlayers(playerRes?.data?.data ?? [])
        setImportInnings(
          importInningsOptions.map((option, i) => ({ ...option, squad: inningsRes[i].data || [] })),
        )
      })
      .catch(() => {
        if (!cancelled) setImportLoadFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [importOpen, importInningsOptions, props.fixtureId, teamId])

  // Everything save-scorecard needs beyond the rows themselves. The endpoint replaces the whole
  // innings, blanking any fixture field missing from its payload, so what is on screen has to
  // travel with every save - and the two bonus fields only when they hold something, since an
  // import or a clear must not wipe a Player of the Match nobody touched on this visit.
  const buildSavePayload = (data, inning) => {
    const payload = {
      data,
      fixtureId: props.fixtureId,
      inning,
      status: statusRef.current,
      scorcard_link_name: formik.values.scorcard_link_name,
      scorcard_link_url: formik.values.scorcard_link_url,
      fall_of_wickets: formik.values.fall_of_wickets,
      match_report: formik.values.match_report,
    }
    if (props.selectedValue?.length) payload.potm = props.selectedValue
    if (formik.values.potm_match_status) payload.potm_match_status = formik.values.potm_match_status
    return payload
  }

  // Clearing empties the form, not the scorecard: nothing is written until Save scores is pressed,
  // so a clear by accident costs a page reload rather than an innings. Every stat cell goes blank
  // and the fantasy points with them, since the points on screen belong to figures that are no
  // longer there - the server works them out again on the next save.
  //
  // The grid's inputs are uncontrolled: each one takes its value from the row it was mounted with
  // and keeps it. So the blanked rows go in under a new key, remounting the grid, which is what
  // actually empties the boxes.
  const [clearedRows, setClearedRows] = useState(null)
  const [gridGeneration, setGridGeneration] = useState(0)

  // Whatever the page fetches next - the other innings, or this one saved - replaces the blanking.
  useEffect(() => {
    setClearedRows(null)
  }, [props.scorecardDetails])

  const remountGrid = () => setGridGeneration((generation) => generation + 1)

  const clearInningScores = () => {
    const blanks = Object.fromEntries(
      (props.scorecardColoumns || [])
        .filter((column) => column?.attribute_key)
        .map((column) => [column.attribute_key, '']),
    )
    const blanked = (props.scorecardDetails || []).map((row) => ({
      ...row,
      ...blanks,
      fantasy_points: '',
    }))

    setClearedRows(blanked)
    remountGrid()
    // What Save scores sends is formik's copy, so it has to be blanked too - the boxes on screen
    // are only half of it.
    formik.setFieldValue('data', blanked)
  }

  // The scorecard as it was fetched is still in props, so undoing is just putting it back.
  const undoClear = () => {
    setClearedRows(null)
    remountGrid()
    formik.setFieldValue('data', props.scorecardDetails || [])
  }

  // The import commits through save-scorecard, the same endpoint the Save scores button uses,
  // because that is what recalculates fantasy points. The whole innings is sent back with the
  // imported figures merged into it: save-scorecard blanks any stat column missing from its
  // payload, so an untouched player has to be sent with the values already on the scorecard.
  //
  // One call per innings, one after the other: save-scorecard stamps every row it is handed with
  // the innings on the payload, so the two innings of a 2-day match cannot travel together, and
  // each call recalculates the fixture's points off what the one before it wrote.
  //
  // Anyone the file named who is not in the squad is added to it first, through the same endpoint
  // the Squad screen uses - that is what creates their scorecard rows, one per innings the fixture
  // has. Every innings is then read back so the figures are written onto the rows as they now
  // stand, including the ones just created.
  const runScoreImport = async (groups) => {
    const errors = []
    props.setLoader(true)

    const inSquad = new Set(
      importInnings.flatMap((entry) => (entry.squad || []).map((row) => String(row.player_id))),
    )
    const missingPlayerIds = [
      ...new Set(
        groups
          .flatMap((group) => group.updates)
          .map((update) => String(update.playerId))
          .filter((playerId) => !inSquad.has(playerId)),
      ),
    ]

    if (missingPlayerIds.length) {
      try {
        const res = await TeamPlayerService.saveMultiPlayer({
          playerId: missingPlayerIds,
          fixtureId: props.fixtureId,
          teamId,
        })
        if (res.status !== 200) {
          props.setLoader(false)
          return {
            success: false,
            errors: [res.message || 'Players missing from the squad could not be added.'],
          }
        }
      } catch (e) {
        props.setLoader(false)
        return {
          success: false,
          errors: ['Players missing from the squad could not be added. Please try again.'],
        }
      }
    }

    for (const group of groups) {
      // eslint-disable-next-line no-await-in-loop
      const detail = await ScorecardService.scorecardDetail(props.fixtureId, group.inning, teamId)
      if (detail?.status !== 200) {
        errors.push(`${group.label}: the scorecard could not be read, so nothing was saved.`)
        continue
      }

      const valuesByPlayer = new Map(
        group.updates.map((update) => [String(update.playerId), update.values]),
      )
      const written = new Set()
      const data = (detail.data || []).map((row) => {
        const values = valuesByPlayer.get(String(row.player_id))
        if (!values) return { ...row }
        written.add(String(row.player_id))
        return { ...row, ...values }
      })

      // A player with no row in this innings has nowhere for their figures to go. Name them
      // rather than report a clean import.
      const missing = group.updates
        .filter((update) => !written.has(String(update.playerId)))
        .map((update) => update.playerName || update.playerId)
      if (missing.length) {
        errors.push(
          `${group.label}: ${missing.join(', ')} ${
            missing.length === 1 ? 'has' : 'have'
          } no scorecard row, so those figures could not be saved.`,
        )
      }

      const payload = buildSavePayload(data, group.inning)

      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await ScorecardService.saveScorecard(payload)
        if (res.status !== 200) {
          errors.push(`${group.label}: ${res.message || 'Something went wrong.'}`)
        }
      } catch (e) {
        errors.push(`${group.label}: something went wrong. Please try again.`)
      }
    }

    if (errors.length) {
      props.setLoader(false)
      return { success: false, errors }
    }

    ToastComponent('Scores imported successfully.', 'success')
    setImportOpen(false)
    props.fetchData(defaultInning)
    return { success: true }
  }

  const importCard = (
    <CollapsibleImportCard
      title="Import Player Scores"
      description={
        isTwoDay
          ? 'Upload a spreadsheet of this match’s figures — both innings in one file — map each row to a player in this game, then import.'
          : 'Upload a spreadsheet of this innings’ figures, map each row to a player in this game, then import.'
      }
      open={importOpen}
      onToggle={() => setImportOpen((open) => !open)}
    >
      {importLoadFailed && (
        <InfoBox color="light">
          This game&rsquo;s players and scorecard could not be loaded, so nothing can be imported
          yet. Close this panel and open it again to retry.
        </InfoBox>
      )}
      {!importLoadFailed && !importInnings.length && <Loader loaderText="Loading players" />}
      {!importLoadFailed && importInnings.length > 0 && (
        <ScorecardImport
          columns={props.scorecardColoumns}
          players={importPlayers}
          innings={importInnings}
          onImport={runScoreImport}
          onCancel={() => setImportOpen(false)}
        />
      )}
    </CollapsibleImportCard>
  )

  const scorecardTitle = props.fixtureDetails.scorcard_link_url ? (
    <Link to={props.fixtureDetails.scorcard_link_url} target="_blank">
      Player stats
    </Link>
  ) : (
    'Player stats'
  )

  const inningsSelect = props.fixtureDetails?.match_type_name === '2-Day' && (
    <div className="d-flex align-items-center gap-2">
      <CFormLabel htmlFor="inning" className="mb-0 small fw-semibold text-medium-emphasis">
        Innings
      </CFormLabel>
      <CFormSelect
        aria-label="select inning"
        name="inning"
        onChange={changeInning}
        value={defaultInning}
        id="inning"
        style={{ width: 150 }}
      >
        {INNINGS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </CFormSelect>
    </div>
  )

  const scorecardDetailsModal = (
    <CModal
      visible={detailsModalVisible}
      onClose={() => setDetailsModalVisible(false)}
      aria-labelledby="scorecard-details-modal-title"
    >
      <CModalHeader>
        <CModalTitle id="scorecard-details-modal-title">Scorecard Details</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <InfoBox>
          Where this scorecard came from. Both fields save as you type &mdash; there is no separate
          save step here.
        </InfoBox>
        <CRow className="g-3">
          <CCol xs={12}>
            <CFormLabel htmlFor="scorcard_link_name">Scorecard Source</CFormLabel>
            <CFormInput
              type="text"
              name="scorcard_link_name"
              id="scorcard_link_name"
              placeholder="Scorecard Source"
              value={formik.values.scorcard_link_name ?? ''}
              onChange={(e) => handleScorecardDetailChange(e, 'scorcard_link_name')}
            />
            <div className="form-text">Who published it &mdash; PlayHQ, MyCricket, the club.</div>
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="scorcard_link_url">Scorecard Link</CFormLabel>
            <CFormInput
              type="text"
              name="scorcard_link_url"
              id="scorcard_link_url"
              placeholder="Scorecard Link"
              value={formik.values.scorcard_link_url ?? ''}
              onChange={(e) => handleScorecardDetailChange(e, 'scorcard_link_url')}
            />
            <div className="form-text">
              Added here, the Player stats heading becomes a link to the published scorecard.
            </div>
          </CCol>
          {/* <CCol xs={12}>
            <CFormLabel htmlFor="fall_of_wickets">Fall of Wickets</CFormLabel>
            <CFormTextarea
              id="fall_of_wickets"
              aria-label="Fall of wickets"
              defaultValue={formik.values.fall_of_wickets}
              name={'fall_of_wickets'}
              onChange={(e) => props.handleInputChange(e, 'fall_of_wickets')}
            ></CFormTextarea>
          </CCol> */}
        </CRow>
      </CModalBody>
      <CModalFooter>
        <CButton color="primary" onClick={() => setDetailsModalVisible(false)}>
          Done
        </CButton>
      </CModalFooter>
    </CModal>
  )

  const bonusPointsModal = (
    <CModal
      className="sc-form-modal"
      visible={bonusModalVisible}
      onClose={() => setBonusModalVisible(false)}
      aria-labelledby="bonus-points-modal-title"
    >
      <CModalHeader>
        <CModalTitle id="bonus-points-modal-title">Bonus Points</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <InfoBox>
          Points awarded on top of the stats above. These are saved with the scores &mdash; press
          <strong> Save scores</strong> on the scorecard once you are done here.
        </InfoBox>
        <CRow className="g-3">
          <CCol xs={12}>
            <CFormLabel htmlFor="player_of_the_match">Player Of The Match</CFormLabel>
            <CMultiSelect
              id="player_of_the_match"
              options={props.playerList}
              selectionType="tags"
              name="player"
              optionsMaxHeight={220}
              onChange={props.handleChange}
              value={props.playerList.filter((obj) => props.selectedValue.includes(obj.value))}
            />
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="potm_match_status">Match Result</CFormLabel>
            <CFormSelect
              id="potm_match_status"
              value={formik.values.potm_match_status}
              onChange={formik.handleChange}
            >
              <option>Select Match Result</option>
              <option value="1">Won</option>
              <option value="2">Other</option>
            </CFormSelect>
          </CCol>
        </CRow>
      </CModalBody>
      <CModalFooter>
        <CButton color="primary" onClick={() => setBonusModalVisible(false)}>
          Done
        </CButton>
      </CModalFooter>
    </CModal>
  )

  const matchStatusCard = (
    <CCard className="mt-3">
      <CCardHeader>
        <strong>Match status</strong>
        <div className="text-medium-emphasis small">
          The last step - set this once the scores above are saved. Choosing Completed opens a final
          review of the saved scorecard.
        </div>
      </CCardHeader>
      <CCardBody>
        <CRow>
          <CCol md={6}>
            <div className="d-flex align-items-center gap-2">
              <CFormSelect
                aria-label="Select Structure"
                name="match_status"
                value={statusValue}
                id="match_status"
                onChange={(e) => {
                  formik.handleChange(e)
                  handleMatchStatus(e)
                }}
              >
                <option value={0}>Select Match Status </option>
                <option value={1}>In-Progress</option>
                <option value={2}>Completed</option>
              </CFormSelect>
              <CTooltip content="What the match statuses mean">
                <CButton
                  color="secondary"
                  variant="ghost"
                  onClick={() => props.setVisible(!props.visible)}
                  aria-label="Match status help"
                >
                  <CIcon icon={cilInfo} />
                </CButton>
              </CTooltip>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  )

  return commonScorecardLoading ? (
    <Loader />
  ) : (
    <>
      <CForm onSubmit={formik.handleSubmit}>
        <div className="ge-page-header">
          <button type="button" className="ge-back-link" onClick={() => navigate(-1)}>
            <CIcon icon={cilArrowLeft} />
            Manage Scores
          </button>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
            <div>
              <h1 className="ge-page-title">Scorecard &mdash; {props.fixtureDetails.team_name}</h1>
              <p className="ge-page-subtitle">
                Enter each player&rsquo;s stats for this innings. Fantasy points are recalculated
                when you save.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <CButton
                color="secondary"
                variant="outline"
                href={`/manage-scores/team-players/${props.fixtureId}/${teamId}`}
              >
                <CIcon icon={cilList} className="me-1" />
                Squad
              </CButton>
              <CButton color="secondary" variant="outline" onClick={props.handleInfoClick}>
                <CIcon icon={cilInfo} className="me-1" />
                Column legend
              </CButton>
            </div>
          </div>
        </div>

        <div className="ge-meta-strip">
          <div className="ge-meta-item">
            <span className="ge-meta-label">Round</span>
            <span className="ge-meta-value">{props.fixtureDetails.round}</span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Team</span>
            <span className="ge-meta-value">{props.fixtureDetails.team_name}</span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Starts</span>
            <span className="ge-meta-value">
              {moment(props.fixtureDetails.start_date).format('D MMM YYYY')}
              {', '}
              {moment(props.fixtureDetails.start_time, ['HH:mm']).format('h:mma')}
            </span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Ends</span>
            <span className="ge-meta-value">
              {moment(props.fixtureDetails.end_date).format('D MMM YYYY')}
              {', '}
              {moment(props.fixtureDetails.end_time, ['HH:mm']).format('h:mma')}
            </span>
          </div>
        </div>

        <div className="sc-toolbar">
          <div className="d-flex flex-wrap align-items-center gap-3">{inningsSelect}</div>
          <div className="d-flex align-items-center gap-2">
            <span className="small fw-medium text-medium-emphasis">
              {enteredCount} of {playerCount} players have scores saved
            </span>
            <div className="sc-progress-track">
              <div className="sc-progress-fill" style={{ width: `${enteredPercent}%` }} />
            </div>
          </div>
        </div>

        {importCard}

        <CCard className="mb-4">
          <CCardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <strong>{scorecardTitle}</strong>
            <div className="sc-header-actions">
              <CTooltip content="Scorecard Details">
                <CButton
                  color="secondary"
                  variant="ghost"
                  className="sc-icon-btn"
                  onClick={() => setDetailsModalVisible(true)}
                  aria-label="Scorecard Details"
                >
                  <CIcon icon={cilLink} size="lg" />
                  {scorecardDetailsSet && <span className="sc-icon-btn-dot" aria-hidden="true" />}
                </CButton>
              </CTooltip>
              <CTooltip content="Bonus Points">
                <CButton
                  color="secondary"
                  variant="ghost"
                  className="sc-icon-btn"
                  onClick={() => setBonusModalVisible(true)}
                  aria-label="Bonus Points"
                >
                  <CIcon icon={cilStar} size="lg" />
                  {bonusPointsSet && <span className="sc-icon-btn-dot" aria-hidden="true" />}
                </CButton>
              </CTooltip>
            </div>
          </CCardHeader>
          <CCardBody>
            {props.loader ? (
              <Loader />
            ) : (
              <ScorecardGrid
                key={gridGeneration}
                columns={props.scorecardColoumns}
                rows={clearedRows ?? props.scorecardDetails}
                isMobile={props.isMobile}
                onChange={formik.handleChange}
                formatFantasyPoints={props.formatFantasyPoints}
              />
            )}
          </CCardBody>
        </CCard>

        <div className="sc-savebar sc-savebar--pinned">
          {clearedRows ? (
            <span className="small text-danger-emphasis">
              {clearTargetLabel === 'this innings' ? 'This innings' : clearTargetLabel} has been
              cleared on screen. Nothing is saved until you press Save scores &mdash; leave the page
              instead and the scores stay as they were.
            </span>
          ) : (
            <span className="small text-medium-emphasis">
              {enteredCount} of {playerCount} players have scores saved. Saving recalculates fantasy
              points for this innings.
            </span>
          )}
          <div className="d-flex flex-wrap align-items-center gap-2">
            {clearedRows ? (
              <CButton type="button" color="secondary" variant="outline" onClick={undoClear}>
                <CIcon icon={cilReload} className="me-1" />
                Undo clear
              </CButton>
            ) : (
              <CButton
                type="button"
                color="danger"
                variant="outline"
                disabled={props.loader || !playerCount}
                onClick={clearInningScores}
                title={`Blank every score on ${clearTargetLabel} without saving`}
              >
                <CIcon icon={cilReload} className="me-1" />
                Clear scores
              </CButton>
            )}
            <CLoadingButton type="submit" color="primary" loading={props.loader}>
              Save scores
            </CLoadingButton>
          </div>
        </div>

        {matchStatusCard}
      </CForm>

      {scorecardDetailsModal}
      {bonusPointsModal}

      <CompleteMarkModal visible={visible} setVisible={setVisible} />
      <MarkAsCompleteModal
        completMarkModalVisible={completMarkModalVisible}
        setCompletMarkModalVisible={setCompletMarkModalVisible}
        data={completeMarkModalData}
        fixtureId={param.fixtureId}
        finalScorecardModalVisible={finalScorecardModalVisible}
        setFinalScorecardModalVisible={setFinalScorecardModalVisible}
      />
      <Bootbox
        show={showConfirmation}
        type={'confirm'}
        message={
          'You are exiting without saving unsaved data. Are you sure you want to discard the changes?'
        }
        onSuccess={handleResetBackConfirm}
        onCancel={handleResetBackCancel}
        onClose={handleResetBackCancel}
        buttons={{
          confirm: {
            label: 'Save & Exit', // Customize the button label here
            className: 'btn-primary', // Optionally, change the button class for styling
          },
          cancel: {
            label: 'Discard & Exit', // Customizing 'Cancel' button
            className: 'btn-danger', // Optional: Add custom class for styling
          },
        }}
      />
    </>
  )
}

export default CommonScorecard
